import { CASE_TYPE_OPTIONS, EDT_ACTIVITY_OPTIONS, procedureLabel } from '@iculogbook/shared';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { errorCopy } from '../auth/errorCopy.ts';
import { ApiError, NetworkError } from '../auth/api.ts';
import { Button, Card, Field, StatTile, TextInput } from '../components/index.ts';
import { adminApi } from '../features/admin/adminApi.ts';
import type { AdminMetrics } from '../features/admin/adminApi.ts';
import {
  ActivityHeatmap,
  CumulativeChart,
  Doughnut,
  shortDay,
  SimpleBarChart,
} from '../features/dashboard/charts.tsx';
import { topWithOther } from '../features/dashboard/chartTheme.ts';

/** `admission` reads better as `Admission` — the same options the log forms use. */
const CASE_TYPE_LABEL = Object.fromEntries(
  CASE_TYPE_OPTIONS.map((option) => [option.key, option.label]),
);
const EDT_ACTIVITY_LABEL = Object.fromEntries(
  EDT_ACTIVITY_OPTIONS.map((option) => [option.key, option.label]),
);

/**
 * `/admin/metrics` — usage figures across every account, for the operator
 * only. Not linked from anywhere in the app's navigation; reaching it means
 * typing the URL. That is deliberate, not the security boundary — the two
 * real gates are the normal session (this route still sits behind
 * `RequireAuth` in `App.tsx`) and the PIN check below, both enforced by the
 * server on every request. A hidden URL only keeps it off the radar of
 * someone who has no reason to be looking for it.
 *
 * Every figure on this page is described in `adminMetrics.ts`, including
 * what it deliberately does not track.
 */

const ENTRY_KIND_LABEL: Record<string, string> = {
  case: 'Cases',
  procedure: 'Procedures',
  edt: 'EDT / Teaching',
  anaesthetic: 'Anaesthetic cases',
};

export function AdminMetricsRoute() {
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [pin, setPin] = useState('');
  const [status, setStatus] = useState<'idle' | 'unlocking' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleUnlock(event: FormEvent) {
    event.preventDefault();
    setStatus('unlocking');
    setErrorMessage(null);

    try {
      await adminApi.unlock(pin);
      const result = await adminApi.metrics();
      setMetrics(result);
      setStatus('idle');
      setPin('');
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error instanceof NetworkError
          ? 'No connection. Check your signal and try again.'
          : errorCopy(error instanceof ApiError ? error.code : null),
      );
    }
  }

  if (metrics === null) {
    return (
      <div className="mx-auto flex max-w-xs flex-col gap-4 px-4 pt-[calc(4rem+env(safe-area-inset-top))]">
        <h1 className="text-center font-serif text-xl text-text">Operator metrics</h1>
        <form
          onSubmit={(event) => {
            void handleUnlock(event);
          }}
          className="flex flex-col gap-3"
        >
          <Field label="PIN" error={status === 'error' ? (errorMessage ?? undefined) : undefined}>
            <TextInput
              type="password"
              inputMode="numeric"
              autoFocus
              autoComplete="off"
              value={pin}
              onChange={(event) => {
                setPin(event.target.value);
              }}
            />
          </Field>
          <Button type="submit" variant="primary" loading={status === 'unlocking'}>
            Unlock
          </Button>
        </form>
      </div>
    );
  }

  return <MetricsView metrics={metrics} />;
}

function MetricsView({ metrics }: { metrics: AdminMetrics }) {
  const { accounts, traffic, features } = metrics;
  const generated = new Date(metrics.generatedAt);

  return (
    /* Outside AppShell, so it clears the notch itself. See AuthLayout. */
    <div className="flex flex-col gap-5 px-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-16">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-xl text-text">Operator metrics</h1>
        <Button
          variant="ghost"
          onClick={() => {
            void adminApi.lock();
          }}
        >
          Lock
        </Button>
      </div>

      <p className="text-xs text-faint">
        As of {generated.toLocaleString('en-GB')}. Derived entirely from account and entry
        timestamps already stored for other reasons — nothing new is tracked to produce this page.
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Accounts" value={accounts.total} />
        <StatTile label="Active, last 7 days" value={traffic.activeLast7Days} />
        <StatTile label="Active, last 30 days" value={traffic.activeLast30Days} />
        <StatTile label="Active, last 90 days" value={traffic.activeLast90Days} />
      </div>

      <Card title="Consistency, across every account">
        <ActivityHeatmap days={traffic.entriesPerDay} />
        <p className="pt-2 text-xs text-faint">
          The last 16 weeks, every account combined into one total per day — never broken out per
          account. See "How many entries per account" below for the closest this page comes to a
          per-account figure, which is a distribution, not a table naming anyone.
        </p>
      </Card>

      <Card title="Accounts over time">
        <CumulativeChart
          points={accounts.cumulative.map((m) => ({ key: m.month, cumulative: m.total }))}
        />
      </Card>

      <Card title="Sign-ups per week">
        <SimpleBarChart
          bars={accounts.signupsPerWeek.map((w) => ({ label: w.weekStarting, value: w.count }))}
          formatLabel={shortDay}
        />
      </Card>

      <Card title="Retention">
        <p className="text-sm text-text">
          Of {traffic.retention90dSignupVs30dActive.eligible} accounts at least 90 days old,{' '}
          {traffic.retention90dSignupVs30dActive.active} logged something in the last 30 days.
        </p>
        <p className="pt-2 text-xs text-faint">
          A floor, not a verdict — a quiet account and an abandoned one look the same from entry
          timestamps alone.
        </p>
      </Card>

      <Card title="How many entries per account">
        <SimpleBarChart
          bars={accounts.entryCountBands.map((b) => ({ label: b.band, value: b.accounts }))}
        />
      </Card>

      <Card title="What gets logged">
        <Doughnut
          title="Entries"
          slices={topWithOther(
            Object.entries(features.entryKindCounts).map(([kind, count]) => ({
              label: ENTRY_KIND_LABEL[kind] ?? kind,
              count,
            })),
          )}
          total={Object.values(features.entryKindCounts).reduce((sum, count) => sum + count, 0)}
        />
      </Card>

      <RankedList
        title="Most-used procedures"
        rows={features.topProcedures.map((row) => ({ ...row, key: procedureLabel(row.key) }))}
      />
      <RankedList
        title="Case types"
        rows={features.topCaseTypes.map((row) => ({
          ...row,
          key: CASE_TYPE_LABEL[row.key] ?? row.key,
        }))}
      />
      <RankedList
        title="EDT / Teaching activities"
        rows={features.topEdtActivities.map((row) => ({
          ...row,
          key: EDT_ACTIVITY_LABEL[row.key] ?? row.key,
        }))}
      />
    </div>
  );
}

/**
 * A ranked list rather than a bar chart — `topProcedures` alone can run to
 * fifteen rows, and fifteen bars is a chart nobody reads at a glance. Each
 * row instead carries a thin proportional bar behind its own text, so
 * relative magnitude is visible without turning this into a second chart
 * form to learn.
 */
function RankedList({ title, rows }: { title: string; rows: { key: string; count: number }[] }) {
  if (rows.length === 0) return null;

  const max = Math.max(1, ...rows.map((row) => row.count));

  return (
    <Card title={title}>
      <ul className="flex flex-col gap-1.5">
        {rows.map((row) => (
          <li key={row.key} className="relative overflow-hidden rounded-sm">
            <div
              aria-hidden="true"
              className="absolute inset-y-0 left-0 bg-accent/15"
              style={{ width: `${String(Math.max(2, (row.count / max) * 100))}%` }}
            />
            <div className="relative flex items-center justify-between gap-3 px-1.5 py-1 text-sm">
              <span className="min-w-0 truncate text-text">{row.key}</span>
              <span className="shrink-0 text-faint">{row.count}</span>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
